import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, AlertCircle, Loader2, Mail, Users } from 'lucide-react';
import { api, ApiError } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';

type InvitationInfo = {
  invitation: {
    email: string;
    role: 'admin' | 'editor';
    expires_at: number;
    organization: { id: string; name: string };
    inviter: { name: string | null; email: string };
  };
};

export function InviteAcceptPage() {
  const { token } = useParams({ strict: false }) as { token: string };
  const navigate = useNavigate();

  const info = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => api.get<InvitationInfo['invitation']>(`/api/auth/invitations/${token}`).then((r) => r as unknown as InvitationInfo['invitation']),
    retry: false,
  });

  // useQuery returns {invitation: ...} actually because backend wraps in invitation. Let me normalize
  const invitation = (info.data as unknown as { invitation: InvitationInfo['invitation'] })?.invitation
    ?? (info.data as unknown as InvitationInfo['invitation']);

  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (invitation?.email) {
      const local = invitation.email.split('@')[0];
      setFullName((prev) => prev || local.charAt(0).toUpperCase() + local.slice(1));
    }
  }, [invitation?.email]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post<{ joined: boolean }>(`/api/auth/invitations/${token}/accept`, {
        password: password || undefined,
        full_name: fullName || undefined,
      });
      navigate({ to: '/dashboard' });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Davet kabul edilemedi');
    } finally {
      setBusy(false);
    }
  };

  if (info.isPending) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (info.isError || !invitation) {
    const message = info.error instanceof ApiError ? info.error.message : 'Davet bulunamadı veya süresi dolmuş';
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-destructive" /> Davet Geçersiz
            </CardTitle>
            <CardDescription>{message}</CardDescription>
          </CardHeader>
          <CardFooter>
            <Button asChild variant="outline">
              <Link to="/login">Giriş sayfasına dön</Link>
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Organizasyona Davet
          </CardTitle>
          <CardDescription>
            <strong>{invitation.inviter.name ?? invitation.inviter.email}</strong> seni{' '}
            <strong>{invitation.organization.name}</strong> organizasyonuna{' '}
            <strong>{invitation.role === 'admin' ? 'yönetici' : 'editör'}</strong> olarak davet etti.
          </CardDescription>
        </CardHeader>
        <form onSubmit={onSubmit}>
          <CardContent className="space-y-4">
            <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm flex items-center gap-2">
              <Mail className="w-4 h-4 text-muted-foreground" />
              <span>{invitation.email}</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Eğer bu e-posta ile zaten hesabın varsa otomatik katılırsın. Yoksa aşağıdan hesap oluştur.
            </p>
            <div className="space-y-2">
              <Label htmlFor="inv-full-name">Ad Soyad</Label>
              <Input
                id="inv-full-name"
                placeholder="İsim Soyisim"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="inv-password">
                Şifre <span className="text-muted-foreground text-xs">(yeni hesap için)</span>
              </Label>
              <Input
                id="inv-password"
                type="password"
                minLength={8}
                placeholder="en az 8 karakter"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Zaten hesabın varsa bu alanı boş bırakabilirsin.
              </p>
            </div>
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 rounded-md border border-destructive/30 px-3 py-2">
                {error}
              </div>
            )}
          </CardContent>
          <CardFooter>
            <Button type="submit" className="w-full" disabled={busy}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Daveti Kabul Et
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
